import React from "react";
import DividerMotif from "../components/Brand/DividerMotif";
import Footer from "../components/Layout/Footer";
import { PublicHeader } from "../components/Layout/PublicHeader";
import { useDocumentMeta } from "../lib/useDocumentMeta";

/*
 * Velaigal /security page.
 *
 * The distinctive moment: the six commitments are not a card grid. They
 * are typeset as a numbered legal-document ledger (Article I through
 * Article VI). Each article has a Roman numeral in the gutter, a hairline
 * rule above, and a small "Effective" stamp at the bottom. Reads as
 * binding text, not as a marketing surface.
 *
 * The hero is anchored by a single monumental geometric vault mark
 * derived from the Velaigal mark, scaled into the page as the visual
 * center of gravity. No icons, no badges, no chips on the page.
 */

const COMMITMENTS = [
  {
    numeral: "I",
    title: "Encryption in transit",
    body:
      "All traffic is encrypted in transit with TLS. Application secrets are kept in server environment configuration, never committed to source control. As a self-hosted, early-stage product we have not yet implemented at-rest disk encryption or a formal key-management service — those are on our roadmap as we mature the infrastructure.",
    stamp: "Standing policy",
  },
  {
    numeral: "II",
    title: "Honest security posture",
    body:
      "We follow established security practices: encrypted transport, access controls, and least-privilege access to candidate data. As an early-stage product, we have not yet pursued formal certification such as SOC 2 or ISO 27001. We will say so plainly if that changes.",
    stamp: "Standing policy",
  },
  {
    numeral: "III",
    title: "GDPR and DPDP-aware",
    body:
      "Indian DPDP Act compliance is the default posture, with GDPR as the export. We honor data subject rights — access, correction, deletion — on request: email the team and we handle it directly, by hand, today. There is no self-serve rights dashboard yet and no regional residency menu; Velaigal runs on a single self-hosted server.",
    stamp: "Standing policy",
  },
  {
    numeral: "IV",
    title: "Password authentication and sessions",
    body:
      "Accounts use email and password today, hashed with argon2id. Sessions are server-side and opaque, expiring after a period of inactivity. We do not yet offer multi-factor authentication — it's on our roadmap, and we won't pretend otherwise.",
    stamp: "Standing policy",
  },
  {
    numeral: "V",
    title: "Role-based access",
    body:
      "Three roles: job seeker, recruiter, and admin. Access control is deny-by-default — the wrong role or the wrong tenant gets a 404, not a message that confirms what exists. An audit log records administrative actions (disabling, enabling, or deleting a user; marking a contact or demo request handled). It does not track which recruiter viewed which candidate, and there is no export tooling for it today.",
    stamp: "Standing policy",
  },
  {
    numeral: "VI",
    title: "Infrastructure and resilience",
    body:
      "Hosted on self-managed infrastructure on our own server, running a PostgreSQL database with regular backups. This is a single-operator, early-stage setup — we have not yet built out multi-node redundancy, dedicated DDoS protection, or a formal restore-drill programme, and we will not claim otherwise.",
    stamp: "Standing policy",
  },
];

const PRINCIPLES = [
  {
    title: "Collect less",
    body:
      "We ask for the minimum data needed to score a candidate. Resume and the work you submit — a link, a repo, a document, or a video. That is the list. New fields require a written justification.",
  },
  {
    title: "Use it for one thing",
    body:
      "Candidate data is used for the role they applied to. No silent enrichment, no resale, no third-party sharing, and no model training without explicit, revocable opt-in.",
  },
  {
    title: "Let candidates own it",
    body:
      "Ask us to export, correct, or delete your data and we'll do it — email the team. We're early-stage and handle these by hand today, not through a self-serve dashboard.",
  },
  {
    title: "Tell the truth when something breaks",
    body:
      "Every customer-affecting incident gets a public timeline within twenty-four hours. We publish what happened, what we have done about it, and what we are changing.",
  },
];

const Security: React.FC = () => {
  useDocumentMeta(
    "Security · Velaigal",
    "How Velaigal protects candidate data: TLS in transit, role-based access, an admin audit log, and an honest early-stage security posture."
  );

  return (
    <div className="min-h-screen w-full bg-cream text-ink">
      <PublicHeader />

      {/* ---------------- Hero with monumental vault mark ----------------
       * The vault mark is a geometric derivative of the Velaigal mark,
       * scaled to clamp(180px, 16vw, 280px). Sits to the right of the
       * headline as the visual anchor of the page.
       */}
      <section className="border-b border-hairline">
        <div className="max-w-7xl mx-auto px-8 pt-20 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-8">
            <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
              Security
            </span>
            <h1
              className="font-display text-4xl md:text-5xl lg:text-6xl font-medium text-ink leading-[1.05] tracking-tight mt-6 max-w-3xl"
              style={{
                fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0',
              }}
            >
              Candidate data deserves a vault, not a marketing badge.
            </h1>
            <span
              aria-hidden="true"
              className="block w-16 h-px bg-saffron mt-8"
            />
            <p className="mt-8 text-lg text-ink-secondary max-w-xl leading-relaxed">
              Hiring data is sensitive. Salaries, performance, references, the
              working that gets a person hired. Below is exactly how Velaigal
              treats it, written as commitments, not as marketing.
            </p>
            <div className="mt-10 flex flex-wrap items-baseline gap-x-8 gap-y-4 text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
              <span>Effective 01 Jan 2026</span>
              <span className="hidden sm:inline-block w-px h-3 bg-hairline-strong align-middle" />
              <span>Document v1.0</span>
              <span className="hidden sm:inline-block w-px h-3 bg-hairline-strong align-middle" />
              <span>Filed under: candidate trust</span>
            </div>
          </div>

          {/* Monumental vault mark: geometric, single saffron stroke */}
          <div className="lg:col-span-4 flex items-start justify-center lg:justify-end">
            <VaultMark />
          </div>
        </div>
      </section>

      {/* ---------------- Articles (the legal-document ledger) ---------------- */}
      <section>
        <div className="max-w-6xl mx-auto px-8 pt-24 pb-16">
          <div className="flex items-baseline justify-between flex-wrap gap-4">
            <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
              Six commitments
            </span>
            <span className="text-[11px] uppercase tracking-widest text-hairline-strong font-medium">
              Articles I to VI
            </span>
          </div>
          <h2
            className="font-display text-3xl lg:text-4xl font-medium text-ink leading-tight mt-6 max-w-2xl velaigal-headline-hover"
            style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
          >
            What we are bound to.
          </h2>
        </div>

        <div className="max-w-6xl mx-auto px-8 pb-24">
          <ol className="list-none p-0 m-0">
            {COMMITMENTS.map((article, idx) => (
              <li
                key={article.numeral}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-x-8 gap-y-6 py-12 ${
                  idx === 0 ? "border-t-2 border-ink" : "border-t border-hairline"
                }`}
              >
                {/* Gutter: Roman numeral and Article label */}
                <div className="lg:col-span-3">
                  <span className="block text-[11px] uppercase tracking-widest text-hairline-strong font-medium">
                    Article
                  </span>
                  <span
                    className="block font-display text-7xl lg:text-8xl font-medium leading-none mt-3"
                    style={{
                      WebkitTextStroke: "1px var(--color-ink)",
                      WebkitTextFillColor: "transparent",
                      fontVariationSettings:
                        '"opsz" 144, "SOFT" 50, "WONK" 0',
                      letterSpacing: "-0.04em",
                    }}
                  >
                    {article.numeral}
                  </span>
                </div>

                {/* Title + body */}
                <div className="lg:col-span-9">
                  <h3
                    className="font-display text-2xl lg:text-3xl font-medium text-ink leading-snug velaigal-headline-hover"
                    style={{
                      fontVariationSettings:
                        '"opsz" 144, "SOFT" 50, "WONK" 0',
                    }}
                  >
                    {article.title}
                  </h3>
                  <p className="mt-5 text-base lg:text-lg text-ink-secondary leading-relaxed max-w-3xl">
                    {article.body}
                  </p>

                  {/* Article footer: stamp + signature line */}
                  <div className="mt-8 pt-5 border-t border-hairline flex items-end justify-between gap-6">
                    <span className="inline-flex items-center gap-3 text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
                      <span
                        aria-hidden="true"
                        className="inline-block w-1.5 h-1.5 rounded-full bg-saffron"
                      />
                      {article.stamp}
                    </span>
                    <span className="hidden sm:flex items-end gap-3 text-[11px] uppercase tracking-widest text-hairline-strong font-medium min-w-[180px]">
                      <span className="flex-1 h-px bg-hairline-strong block" />
                      <span>Engineering &amp; ops</span>
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <DividerMotif className="py-2" />

      {/* ---------------- Privacy principles ---------------- */}
      <section>
        <div className="max-w-7xl mx-auto px-8 py-24">
          <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
            Privacy principles
          </span>
          <h2
            className="font-display text-3xl lg:text-4xl font-medium text-ink leading-tight mt-6 max-w-3xl velaigal-headline-hover"
            style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
          >
            Four rules we do not bend.
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-hairline">
            {PRINCIPLES.map((principle, idx) => (
              <div key={principle.title} className="bg-cream p-10 lg:p-12">
                <div className="flex items-baseline gap-4">
                  <span className="font-display text-base text-saffron font-medium">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <h3
                    className="font-display text-xl lg:text-2xl font-medium text-ink leading-snug velaigal-headline-hover"
                    style={{
                      fontVariationSettings:
                        '"opsz" 144, "SOFT" 50, "WONK" 0',
                    }}
                  >
                    {principle.title}
                  </h3>
                </div>
                <p className="mt-5 text-base text-ink-secondary leading-relaxed max-w-md">
                  {principle.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <DividerMotif className="py-2" />

      {/* ---------------- Responsible disclosure ---------------- */}
      <section>
        <div className="max-w-7xl mx-auto px-8 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <span className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
                Found something?
              </span>
              <h2
                className="font-display text-3xl lg:text-4xl font-medium text-ink leading-tight mt-6 velaigal-headline-hover"
                style={{
                  fontVariationSettings:
                    '"opsz" 144, "SOFT" 50, "WONK" 0',
                }}
              >
                Responsible disclosure.
              </h2>
              <span
                aria-hidden="true"
                className="block w-12 h-px bg-saffron mt-8"
              />
            </div>
            <div className="lg:col-span-7 lg:pl-12 lg:border-l lg:border-hairline">
              <p className="text-base lg:text-lg text-ink-secondary leading-relaxed">
                Email{" "}
                <a
                  href="mailto:im.vmanoj2002@gmail.com"
                  className="velaigal-link"
                  style={{ textDecoration: "none" }}
                >
                  im.vmanoj2002@gmail.com
                </a>{" "}
                with the subject line "Security". We acknowledge within one
                business day, fix critical issues within seven, and never
                threaten researchers acting in good faith.
              </p>
              <dl className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-hairline pt-8">
                <div>
                  <dt className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
                    Acknowledge
                  </dt>
                  <dd
                    className="font-display text-3xl text-ink mt-3"
                    style={{
                      fontVariationSettings:
                        '"opsz" 144, "SOFT" 50, "WONK" 0',
                    }}
                  >
                    1 day
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
                    Critical fix
                  </dt>
                  <dd
                    className="font-display text-3xl text-ink mt-3"
                    style={{
                      fontVariationSettings:
                        '"opsz" 144, "SOFT" 50, "WONK" 0',
                    }}
                  >
                    7 days
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] uppercase tracking-widest text-ink-tertiary font-medium">
                    Public timeline
                  </dt>
                  <dd
                    className="font-display text-3xl text-ink mt-3"
                    style={{
                      fontVariationSettings:
                        '"opsz" 144, "SOFT" 50, "WONK" 0',
                    }}
                  >
                    24 hrs
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

/*
 * Monumental vault mark.
 *
 * Geometric construction, single saffron stroke on the keyhole, ink
 * hairlines for the rest. Built as inline SVG so the strokes stay crisp
 * at any size and inherit currentColor in the parent.
 *
 * The shape: a softly squared "vault door" outline (12px corner radius
 * on a 200x200 viewbox), a horizontal threshold band a third of the way
 * down, a centered keyhole composed of a circle + tapering stem, and
 * three saffron tick marks at the right edge as a hinge motif.
 */
const VaultMark: React.FC = () => {
  return (
    <svg
      role="img"
      aria-label="Vault mark"
      viewBox="0 0 200 200"
      style={{
        width: "clamp(180px, 16vw, 280px)",
        height: "clamp(180px, 16vw, 280px)",
        display: "block",
      }}
    >
      {/* Outer vault outline */}
      <rect
        x="14"
        y="14"
        width="172"
        height="172"
        rx="6"
        ry="6"
        fill="none"
        stroke="var(--color-ink)"
        strokeWidth="1.25"
      />
      {/* Inner inset frame: gives the embossed-door feel */}
      <rect
        x="26"
        y="26"
        width="148"
        height="148"
        rx="3"
        ry="3"
        fill="none"
        stroke="var(--color-hairline-strong)"
        strokeWidth="1"
      />
      {/* Threshold band */}
      <line
        x1="26"
        y1="76"
        x2="174"
        y2="76"
        stroke="var(--color-hairline-strong)"
        strokeWidth="1"
      />
      {/* Keyhole: saffron, the single accent */}
      <g>
        <circle
          cx="100"
          cy="116"
          r="11"
          fill="none"
          stroke="var(--color-saffron)"
          strokeWidth="1.75"
        />
        <path
          d="M100 127 L96 148 L104 148 Z"
          fill="var(--color-saffron)"
        />
      </g>
      {/* Hinge ticks on the right edge */}
      <g stroke="var(--color-saffron)" strokeWidth="1.25">
        <line x1="178" y1="56" x2="186" y2="56" />
        <line x1="178" y1="100" x2="186" y2="100" />
        <line x1="178" y1="144" x2="186" y2="144" />
      </g>
      {/* Filed-stamp number, lower-left, in ink hairline (anchors as document) */}
      <text
        x="34"
        y="166"
        fontFamily='"Instrument Sans", system-ui, sans-serif'
        fontSize="9"
        fontWeight="500"
        letterSpacing="2"
        fill="var(--color-ink-tertiary)"
      >
        VLG · SEC · 01
      </text>
    </svg>
  );
};

export default Security;
